<script setup lang="ts">
import type { EditorialFashionShowGridProps } from './EditorialFashionShowGrid.props'
const props = defineProps<EditorialFashionShowGridProps>()

const { ts } = useI18n()

const emit = defineEmits<{
  'clicked-media': [index: number]
}>()

const { isEmporioArmaniWebsite, isEa7Website } = useWebsite()

const { breakpoint } = useProjectBreakpoint()
const isMobile = breakpoint.smaller('md')
const isTablet = breakpoint.between('md', 'lg')
const isDesktop = breakpoint.greater('lg')

const paginationContainer = ref<HTMLElement>()

const currentPage = ref<number>(1)
const showAll = ref<boolean>(false)

// Number of cards displayed per page on each device type
const cardsPerPage = {
  mobile: 6,
  tablet: 10,
  desktop: 12,
}

// Loads the next set of cards when "Show More" is clicked
const handleShowMoreClick = () => {
  currentPage.value += 1
}

// Resets to the first page and scrolls up when "Show Less" is clicked
const handleShowLessClick = () => {
  showAll.value = false
  currentPage.value = 1
  nextTick(() => {
    paginationContainer.value?.scrollIntoView({ block: 'end' })
  })
}

// Shows all cards when "Show All" is clicked
const handleShowAllClick = () => {
  showAll.value = true
}

// Determine the device type based on screen size
const deviceType = computed(() => {
  if (isMobile.value) return 'mobile'
  if (isTablet.value) return 'tablet'
  if (isDesktop.value) return 'desktop'
})

// Check if the current page is the last page
const isLastPage = computed(() => {
  if (!deviceType.value) return false

  return (
    showAll.value ||
    currentPage.value * cardsPerPage[deviceType.value] >= props.cards.length
  )
})

// Determines classes for each card based on pagination and device type
const getCardClasses = (contentIndex: number) => {
  if (showAll.value) return

  return {
    'hidden md:block':
      contentIndex >= cardsPerPage.mobile * currentPage.value &&
      contentIndex < cardsPerPage.tablet * currentPage.value,
    'hidden lg:block':
      contentIndex >= cardsPerPage.tablet * currentPage.value &&
      contentIndex < cardsPerPage.desktop * currentPage.value,
    hidden: contentIndex >= cardsPerPage.desktop * currentPage.value,
  }
}

// Computes the label for "Show More" button with cards count by device type
const showMoreLabel = computed(() => {
  const cardsShown = {
    mobile: cardsPerPage.mobile * currentPage.value,
    tablet: cardsPerPage.tablet * currentPage.value,
    desktop: cardsPerPage.desktop * currentPage.value,
  }

  return {
    mobile: `${ts('plp.showMore')} (${cardsShown.mobile}/${props.cards.length})`,
    tablet: `${ts('plp.showMore')} (${cardsShown.tablet}/${props.cards.length})`,
    desktop: `${ts('plp.showMore')} (${cardsShown.desktop}/${props.cards.length})`,
  }
})
</script>

<template>
  <div
    class="editorial-fashion-show-grid grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6"
  >
    <button
      v-for="(content, contentIndex) in cards"
      :key="`media-${contentIndex}`"
      class="custom-zoom-cursor-add"
      :class="getCardClasses(contentIndex)"
      aria-haspopup="dialog"
      :aria-label="ts('aria-labels.fashionShow')"
      @click="emit('clicked-media', contentIndex)"
    >
      <UIMedia
        v-bind="content.media"
        :aspect-ratio="content.aspectRatio ?? '4/5'"
        class="object-cover"
      />
    </button>
  </div>
  <div
    ref="paginationContainer"
    class="grid justify-items-center gap-6 pt-10 lg:pt-12"
  >
    <template v-if="!isLastPage">
      <AtomsCta
        class="bg-background-primary text-text-secondary"
        :class="{
          'hover:!bg-primitives-grey-100':
            isEmporioArmaniWebsite || isEa7Website,
        }"
        :icon-show-default="false"
        @click="handleShowMoreClick"
      >
        <template #label>
          <span class="md:hidden">{{ showMoreLabel.mobile }}</span>
          <span class="hidden md:block lg:hidden">
            {{ showMoreLabel.tablet }}
          </span>
          <span class="hidden lg:block">{{ showMoreLabel.desktop }}</span>
        </template>
      </AtomsCta>
      <AtomsCta
        anatomy="link"
        :icon-show-default="false"
        @click="handleShowAllClick"
      >
        <template #label>{{ $ts('plp.showAll') }}</template>
      </AtomsCta>
    </template>
    <AtomsCta
      v-else
      anatomy="link"
      :icon-show-default="false"
      @click="handleShowLessClick"
    >
      <template #label>{{ $ts('plp.showLess') }}</template>
    </AtomsCta>
  </div>
</template>

<style scoped lang="scss">
.custom-zoom-cursor-add {
  cursor:
    url('@design-system/icons/AddWBkg.svg') 24 24,
    auto;
}
</style>
